import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";
import {
  Scroll, ArrowLeft, Calculator, Sparkle, Crown, Warning, Star,
  CaretDown, MagnifyingGlass, MoonStars, Sun, Heart, Coins, Briefcase,
  AirplaneTilt, Baby, Brain, GraduationCap, ShieldCheck, ChartLineUp,
  CalendarBlank, ArrowUp, ArrowDown, ArrowsLeftRight, Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const SECTION_ICONS = {
  personality:   Brain,
  health:        Heart,
  wealth:        Coins,
  relationships: Sparkle,
  career:        Briefcase,
  travel:        AirplaneTilt,
  job_gain:      ChartLineUp,
  job_loss:      Warning,
  lottery:       Star,
  children:      Baby,
  spirituality:  GraduationCap,
};

const SECTION_THEME = {
  personality:   "from-amber-50 to-orange-50 border-amber-200",
  health:        "from-rose-50 to-pink-50 border-rose-200",
  wealth:        "from-emerald-50 to-green-50 border-emerald-200",
  relationships: "from-pink-50 to-rose-50 border-pink-200",
  career:        "from-blue-50 to-indigo-50 border-blue-200",
  travel:        "from-sky-50 to-cyan-50 border-sky-200",
  job_gain:      "from-teal-50 to-emerald-50 border-teal-200",
  job_loss:      "from-orange-50 to-red-50 border-orange-200",
  lottery:       "from-yellow-50 to-amber-50 border-yellow-200",
  children:      "from-purple-50 to-violet-50 border-purple-200",
  spirituality:  "from-indigo-50 to-purple-50 border-indigo-200",
};

export default function NadiAstrologyPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("profile"); // 'profile' | 'transits'
  const [form, setForm] = useState({
    date: "", time: "", place: "", timezone: "Asia/Kolkata",
  });
  usePrefillBirthDetails(setForm);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  // Celebrity DB picker (mirrors KP / Lottery Indicator UX)
  const [celebs, setCelebs] = useState([]);
  const [loadedSubject, setLoadedSubject] = useState(null); // {name, source, rise, photo}
  const [celebQuery, setCelebQuery] = useState("");
  const [celebSearchOpen, setCelebSearchOpen] = useState(false);

  // Transit-section state
  const today = new Date();
  const twoYr = new Date();
  twoYr.setFullYear(today.getFullYear() + 2);
  const iso = (d) => d.toISOString().slice(0, 10);
  const [transitForm, setTransitForm] = useState({
    start_date: iso(today),
    end_date: iso(twoYr),
    category: "career",
  });
  const [transitResult, setTransitResult] = useState(null);
  const [transitBusy, setTransitBusy] = useState(false);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const handleT = (k) => (e) => setTransitForm((f) => ({ ...f, [k]: e.target.value }));

  // Lazy-load celebrity list once the super-admin lands on the page
  useEffect(() => {
    if (!user?.is_super_admin) return;
    api.get("/admin/tools/lottery-indicator/celebrities")
      .then(({ data }) => setCelebs(data.celebrities || []))
      .catch(() => { /* picker just won't appear */ });
  }, [user]);

  const loadCelebrity = (slug) => {
    const c = celebs.find((x) => x.slug === slug);
    if (!c) return;
    setForm({
      date: c.dob,
      time: c.time,
      place: c.place,
      timezone: c.tz || "Asia/Kolkata",
    });
    setLoadedSubject({
      name: c.name,
      source: `${(c.category || "").replace("-", " ")} · ${c.rodden_rating}-rated · ${c.country}`,
      rise: c.rise_event_label
        ? `${c.rise_event_label} (${(c.rise_event_date || "").slice(0, 4)})`
        : "",
      photo: c.photo || "",
    });
    setResult(null);
    setTransitResult(null);
    toast.success(`Loaded ${c.name}'s chart.`);
  };

  const clearLoadedSubject = () => {
    setLoadedSubject(null);
    setCelebQuery("");
  };

  // ─── Super-admin gating ─────────────────────────────────────────────
  if (!loading && (!user || !user.is_super_admin)) {
    return (
      <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col">
        <Header />
        <main className="flex-1 max-w-xl mx-auto px-4 py-16 text-center" data-testid="nadi-page-gated">
          <h1 className="font-serif-display text-3xl">Super Admin only</h1>
          <p className="text-muted mt-3">
            This Nadi astrology engine ingests private Bhrigu / Nandi / Agastya
            Nadi reference material and is restricted to admin accounts.
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  const analyze = async (e) => {
    e?.preventDefault();
    if (!form.date || !form.time || !form.place) {
      toast.error("Please enter your date, time and place of birth.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/nadi/profile", form);
      setResult(data);
      setTimeout(() => {
        document.querySelector("[data-testid='nadi-result']")?.scrollIntoView({
          behavior: "smooth", block: "start",
        });
      }, 100);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not compute your Nadi profile. Please re-check your inputs.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const analyzeTransits = async (e) => {
    e?.preventDefault();
    if (!form.date || !form.time || !form.place) {
      toast.error("Please enter your date, time and place of birth first.");
      return;
    }
    if (!transitForm.start_date || !transitForm.end_date) {
      toast.error("Please pick a start and end date for the forecast.");
      return;
    }
    if (transitForm.end_date <= transitForm.start_date) {
      toast.error("End date must be after start date.");
      return;
    }
    setTransitBusy(true);
    setTransitResult(null);
    try {
      const { data } = await api.post("/nadi/transit-forecast", {
        ...form,
        start_date: transitForm.start_date,
        end_date: transitForm.end_date,
        category: transitForm.category,
      });
      setTransitResult(data);
      setTimeout(() => {
        document.querySelector("[data-testid='nadi-transit-result']")?.scrollIntoView({
          behavior: "smooth", block: "start",
        });
      }, 100);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not compute the transit forecast.";
      toast.error(msg);
    } finally {
      setTransitBusy(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col" data-testid="nadi-page">
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <button
          onClick={() => navigate(-1)}
          className="md:hidden flex items-center gap-1 text-sm text-muted mb-4"
          data-testid="nadi-back-btn"
        >
          <ArrowLeft size={16} /> Back
        </button>

        {/* Hero */}
        <div className="flex items-center gap-2 text-amber-700 text-[10px] uppercase tracking-[0.25em] font-semibold">
          <ShieldCheck size={14} weight="duotone" /> Super Admin · Private Engine
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
          Nadi Astrology <span className="italic text-gold">Deep Engine</span>
        </h1>
        <p className="text-muted text-sm sm:text-base mt-4 max-w-3xl font-light">
          A research-grade Nadi reading engine grounded in classical Bhrigu-Nandi
          principles. Reads directly from planet conjunctions, exchanges, and Nadi
          house-distance combinations — bypassing the lagna unless a yoga touches it.
          Detects 50+ classical Bhrigu / Nandi yogas and a day-by-day transit
          forecaster keyed to natal Karaka points.
        </p>

        {/* Tabs */}
        <div
          className="mt-7 inline-flex rounded-full border border-soft bg-white/80 p-1 shadow-sm"
          data-testid="nadi-tabs"
        >
          <button
            onClick={() => setTab("profile")}
            className={`px-4 sm:px-5 py-2 rounded-full text-sm font-medium transition ${
              tab === "profile"
                ? "bg-[#3F2D1B] text-[#FAF4E8] shadow"
                : "text-[#3F2D1B] hover:bg-amber-50"
            }`}
            data-testid="nadi-tab-profile"
          >
            <Scroll size={14} weight="duotone" className="inline mr-1.5" />
            Section 1 · Complete Profile
          </button>
          <button
            onClick={() => setTab("transits")}
            className={`px-4 sm:px-5 py-2 rounded-full text-sm font-medium transition ${
              tab === "transits"
                ? "bg-[#3F2D1B] text-[#FAF4E8] shadow"
                : "text-[#3F2D1B] hover:bg-amber-50"
            }`}
            data-testid="nadi-tab-transits"
          >
            <CalendarBlank size={14} weight="duotone" className="inline mr-1.5" />
            Section 2 · Transit Predictor
          </button>
        </div>

        {/* Birth-details form (shared by both sections) */}
        <BirthDetailsForm
          form={form}
          handle={handle}
          setForm={setForm}
          showSubmit={tab === "profile"}
          busy={busy}
          onSubmit={analyze}
          celebs={celebs}
          loadedSubject={loadedSubject}
          loadCelebrity={loadCelebrity}
          celebQuery={celebQuery}
          setCelebQuery={setCelebQuery}
          celebSearchOpen={celebSearchOpen}
          setCelebSearchOpen={setCelebSearchOpen}
          clearLoadedSubject={clearLoadedSubject}
        />

        {/* Section-2 controls */}
        {tab === "transits" && (
          <form
            onSubmit={analyzeTransits}
            className="mt-5 card-surface rounded-md p-5 sm:p-7 space-y-4"
            data-testid="nadi-transit-form"
          >
            <div className="text-[11px] uppercase tracking-widest text-amber-700 font-semibold flex items-center gap-1.5">
              <CalendarBlank size={13} weight="duotone" /> Forecast window & category
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label className="block">
                <span className="label-eyebrow text-xs">From date</span>
                <input
                  type="date" required
                  value={transitForm.start_date}
                  onChange={handleT("start_date")}
                  className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg"
                  data-testid="nadi-transit-start"
                />
              </label>
              <label className="block">
                <span className="label-eyebrow text-xs">To date</span>
                <input
                  type="date" required
                  value={transitForm.end_date}
                  onChange={handleT("end_date")}
                  className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg"
                  data-testid="nadi-transit-end"
                />
              </label>
              <label className="block">
                <span className="label-eyebrow text-xs">Category</span>
                <select
                  value={transitForm.category}
                  onChange={handleT("category")}
                  className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg"
                  data-testid="nadi-transit-category"
                >
                  <option value="overview">All Domains Overview</option>
                  <option value="career">Career & Profession</option>
                  <option value="wealth">Wealth & Money</option>
                  <option value="relationships">Relationships & Marriage</option>
                  <option value="health">Health & Body</option>
                  <option value="travel">Travel & Foreign</option>
                  <option value="job_gain">Job Gain & Promotion</option>
                  <option value="job_loss">Job Loss & Setback</option>
                  <option value="lottery">Lottery & Sudden Gain</option>
                  <option value="children">Children & Progeny</option>
                  <option value="spirituality">Spirituality & Inner Path</option>
                  <option value="personality">Personality Shifts</option>
                </select>
              </label>
            </div>
            <button
              type="submit"
              disabled={transitBusy}
              className="btn-primary rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-60"
              data-testid="nadi-transit-submit"
            >
              <Calculator size={16} weight="duotone" className="inline mr-2" />
              {transitBusy
                ? "Scanning slow-planet transits..."
                : "Forecast my transit windows"}
            </button>
          </form>
        )}

        {/* Result */}
        {tab === "profile" && result && <NadiResultView data={result} />}
        {tab === "transits" && transitResult && (
          <NadiTransitResultView data={transitResult} />
        )}
      </main>
      <Footer />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Shared birth-details form
// ───────────────────────────────────────────────────────────────────────
function BirthDetailsForm({
  form, handle, setForm, showSubmit, busy, onSubmit,
  celebs = [], loadedSubject = null, loadCelebrity = () => {},
  celebQuery = "", setCelebQuery = () => {},
  celebSearchOpen = false, setCelebSearchOpen = () => {},
  clearLoadedSubject = () => {},
}) {
  return (
    <form
      onSubmit={showSubmit ? onSubmit : (e) => e?.preventDefault()}
      className="mt-7 card-surface rounded-md p-5 sm:p-7 space-y-4"
      data-testid="nadi-form"
    >
      <div className="text-[11px] uppercase tracking-widest text-amber-700 font-semibold flex items-center gap-1.5">
        <Sparkle size={13} weight="duotone" /> Birth details (shared by both sections)
      </div>

      {/* Celebrity picker (super-admin DB · 75+ verified charts) */}
      {celebs.length > 0 && (
        <div className="rounded-lg bg-amber-50/60 border border-amber-200 p-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold">
              Load from Celebrity DB · {celebs.length} verified charts
            </div>
            {loadedSubject && (
              <button
                type="button"
                onClick={clearLoadedSubject}
                className="text-[10px] uppercase tracking-widest text-rose-700 hover:text-rose-900"
                data-testid="nadi-celeb-clear"
              >
                Clear
              </button>
            )}
          </div>
          {loadedSubject ? (
            <div
              className="mt-2 flex items-start gap-3 p-2 rounded-md bg-white border border-amber-200"
              data-testid="nadi-celeb-loaded"
            >
              {loadedSubject.photo && (
                <img
                  src={loadedSubject.photo}
                  alt={loadedSubject.name}
                  className="w-12 h-12 rounded-full object-cover border border-amber-300"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-serif-display text-base sm:text-lg text-[#3F2D1B] leading-tight">
                  {loadedSubject.name}
                </div>
                <div className="text-[11px] text-[#3F2D1B]/70 mt-0.5 truncate">
                  {loadedSubject.source}
                </div>
                {loadedSubject.rise && (
                  <div className="text-[11px] text-amber-700 italic mt-0.5 truncate">
                    ↳ {loadedSubject.rise}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="relative mt-2">
              <input
                type="text"
                value={celebQuery}
                onFocus={() => setCelebSearchOpen(true)}
                onChange={(e) => { setCelebQuery(e.target.value); setCelebSearchOpen(true); }}
                onBlur={() => setTimeout(() => setCelebSearchOpen(false), 180)}
                placeholder="🔎  Search celebrity — Jobs, Ambani, Castro, Chaplin…"
                className="w-full px-3 py-2 rounded-md border border-amber-300 bg-white text-sm"
                data-testid="nadi-celeb-search"
              />
              {celebSearchOpen && (
                <div
                  className="absolute z-50 left-0 right-0 top-full mt-1 max-h-[340px] overflow-y-auto rounded-md shadow-2xl bg-white border border-amber-200"
                  data-testid="nadi-celeb-results"
                >
                  {(() => {
                    const q = celebQuery.trim().toLowerCase();
                    const filtered = q
                      ? celebs.filter((c) =>
                          c.name.toLowerCase().includes(q)
                          || (c.industry || "").toLowerCase().includes(q)
                          || (c.country || "").toLowerCase().includes(q)
                          || c.category.toLowerCase().includes(q)
                          || (c.story || "").toLowerCase().includes(q)
                        ).slice(0, 30)
                      : celebs.slice(0, 12);
                    if (!filtered.length) {
                      return (
                        <div className="px-3 py-4 text-xs text-center text-[#3F2D1B]/60">
                          No celebrity matches &quot;{celebQuery}&quot;.
                        </div>
                      );
                    }
                    const grouped = filtered.reduce((acc, c) => {
                      (acc[c.category] = acc[c.category] || []).push(c);
                      return acc;
                    }, {});
                    return Object.entries(grouped).map(([cat, list]) => (
                      <div key={cat}>
                        <div className="px-3 py-1.5 text-[9px] uppercase tracking-[0.2em] text-amber-800 bg-amber-100 border-b border-amber-200 sticky top-0 font-semibold">
                          {cat.replace("-", " ")} · {list.length}
                        </div>
                        {list.map((c) => (
                          <button
                            type="button"
                            key={c.slug}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              loadCelebrity(c.slug);
                              setCelebQuery(c.name);
                              setCelebSearchOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b border-amber-100 last:border-b-0 flex items-start gap-3"
                            data-testid={`nadi-celeb-opt-${c.slug}`}
                          >
                            {c.photo && (
                              <img
                                src={c.photo}
                                alt={c.name}
                                className="w-8 h-8 rounded-full object-cover border border-amber-200 shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-[#3F2D1B] truncate">
                                {c.name}
                                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">
                                  {c.rodden_rating}
                                </span>
                              </div>
                              <div className="text-[10px] text-[#3F2D1B]/60 mt-0.5 truncate">
                                {c.dob} {c.time} · {c.place || c.country}
                              </div>
                              {c.rise_event_label && (
                                <div className="text-[10px] mt-0.5 italic text-amber-700 truncate">
                                  ↳ {c.rise_event_label} ({(c.rise_event_date || "").slice(0, 4)})
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="label-eyebrow text-xs">Date of birth</span>
          <input
            type="date" required value={form.date} onChange={handle("date")}
            className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg"
            data-testid="nadi-date"
          />
        </label>
        <label className="block">
          <span className="label-eyebrow text-xs">Time of birth</span>
          <input
            type="time" required value={form.time} onChange={handle("time")}
            className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg"
            data-testid="nadi-time"
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="label-eyebrow text-xs">Place of birth</span>
          <div className="mt-1.5">
            <PlaceAutocompleteInput
              value={form.place}
              onChange={(v) => setForm((f) => ({ ...f, place: v }))}
              onPick={({ name, timezone }) =>
                setForm((f) => ({ ...f, place: name, timezone: timezone || f.timezone }))
              }
              placeholder="Mumbai, India"
              testId="nadi-place"
            />
            {form.timezone && (
              <p className="text-[11px] text-muted mt-1">
                Auto-detected timezone: <b>{form.timezone}</b>
              </p>
            )}
          </div>
        </label>
      </div>
      {showSubmit && (
        <button
          type="submit"
          disabled={busy}
          className="btn-primary rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-60"
          data-testid="nadi-submit"
        >
          <Calculator size={16} weight="duotone" className="inline mr-2" />
          {busy
            ? "Reading the leaves... (40-60s, parallel LLM batches)"
            : "Generate my deep Nadi profile"}
        </button>
      )}
    </form>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Result view
// ───────────────────────────────────────────────────────────────────────
function NadiResultView({ data }) {
  const { profile, narrative, section_titles, personalized } = data;
  const sectionOrder = profile.section_order || Object.keys(narrative.sections || {});

  return (
    <div className="mt-10 space-y-7" data-testid="nadi-result">
      {/* Hero card */}
      <HeroCard profile={profile} />

      {/* LLM-degraded warning */}
      {!personalized && (
        <div
          className="rounded-2xl border border-amber-400 bg-amber-50 p-4 sm:p-5 flex items-start gap-3"
          data-testid="nadi-llm-warning"
        >
          <Warning size={20} weight="duotone" className="text-amber-700 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900">
            <b>Narrative LLM unavailable.</b> The structured Nadi engine has
            still detected every applicable yoga from the chart (see the section
            chips and full yoga list below). Top up your Emergent LLM key
            balance to regenerate the deep-dive paragraphs.
          </div>
        </div>
      )}

      {/* Top verdicts */}
      {(narrative.strongest_yoga || narrative.weakest_link) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="nadi-verdicts">
          {narrative.strongest_yoga && (
            <VerdictCard
              tone="strength"
              icon={Crown}
              label="Strongest yoga"
              body={narrative.strongest_yoga}
            />
          )}
          {narrative.weakest_link && (
            <VerdictCard
              tone="weakness"
              icon={Warning}
              label="Karmic curriculum"
              body={narrative.weakest_link}
            />
          )}
        </div>
      )}

      {/* Highlights */}
      {(narrative.highlights || []).length > 0 && (
        <div className="card-surface rounded-2xl p-5 sm:p-7" data-testid="nadi-highlights">
          <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-3">
            <Sparkle size={14} weight="duotone" /> Life-event highlights
          </div>
          <ul className="space-y-2.5">
            {narrative.highlights.map((h, i) => (
              <li
                key={i}
                className="text-sm sm:text-base leading-relaxed text-[#3F2D1B] flex items-start gap-2.5"
                data-testid={`nadi-highlight-${i}`}
              >
                <Star size={16} weight="duotone" className="text-amber-600 mt-1 shrink-0" />
                <span dangerouslySetInnerHTML={{ __html: linkify(h) }} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Degree-chain + Destiny-breaker panels (Nadi-specific) */}
      <DegreeChainPanel chain={profile.degree_chain || []} breakers={profile.destiny_breakers || []} />

      {/* 11 Section Deep-Dives */}
      <div className="space-y-5" data-testid="nadi-sections">
        {sectionOrder.map((sec) => (
          <SectionCard
            key={sec}
            secKey={sec}
            title={section_titles?.[sec] || sec}
            narrative={narrative.sections?.[sec] || ""}
            yogas={profile.sections?.[sec] || []}
          />
        ))}
      </div>

      {/* Raw yogas + planets debug panels */}
      <details
        className="rounded-2xl border border-soft bg-white/60 overflow-hidden"
        data-testid="nadi-debug"
      >
        <summary className="cursor-pointer list-none px-5 py-3 flex items-center gap-2 text-sm text-[#3F2D1B] hover:bg-amber-50/40">
          <MagnifyingGlass size={16} weight="duotone" className="text-gold" />
          Engine internals · planet table + all {profile.yogas_all?.length || 0} detected Nadi yogas
        </summary>
        <div className="px-5 pb-5 pt-2 space-y-5">
          <PlanetTable planets={profile.planets || []} karakas={profile.jaimini_karakas || []} />
          <YogasTable yogas={profile.yogas_all || []} />
        </div>
      </details>
    </div>
  );
}

function HeroCard({ profile }) {
  const lg = profile.lagna || {};
  const mn = profile.moon || {};
  const karakas = profile.jaimini_karakas || [];
  const ak = karakas[0];

  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-[#3F2D1B] via-[#5a3e22] to-[#7c4f2a] text-[#FAF4E8] p-6 sm:p-8 shadow-xl border border-amber-700/40"
      data-testid="nadi-hero"
    >
      <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-widest mb-2">
        <Scroll size={14} weight="fill" /> Your Nadi blueprint
      </div>
      <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight">
        Lagna · {lg.rashi || "—"} <span className="text-amber-300">·</span> Moon · {mn.rashi || "—"}
      </h2>
      <div className="mt-2 text-amber-200/80 text-sm">
        {lg.rashi_lord ? `Lagnesh ${lg.rashi_lord}` : ""} · Janma Nakshatra
        {" "}<b>{mn.nakshatra || "—"}</b> (pada {mn.pada || "—"}, lord {mn.nakshatra_lord || "—"})
      </div>
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {karakas.slice(0, 4).map((k) => (
          <div
            key={k.karaka}
            className="rounded-lg px-3 py-2 bg-amber-900/40 border border-amber-700/30"
            data-testid={`nadi-karaka-${k.karaka}`}
          >
            <div className="text-[10px] uppercase tracking-widest text-amber-300/80">{k.karaka}</div>
            <div className="mt-0.5 font-serif-display text-base sm:text-lg leading-tight text-amber-50">
              {k.planet}
            </div>
            <div className="text-[10px] text-amber-200/70 mt-0.5">
              {k.rashi} · {k.deg_in_sign?.toFixed(1)}°
            </div>
          </div>
        ))}
      </div>
      {ak && (
        <div className="mt-4 text-xs text-amber-200/70">
          Atmakaraka <b className="text-amber-100">{ak.planet}</b> ({ak.rashi}) — the planet whose karma you most carry forward in this incarnation.
        </div>
      )}
    </div>
  );
}

function VerdictCard({ tone, icon: Icon, label, body }) {
  const cls = tone === "strength"
    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
    : "bg-rose-50 border-rose-200 text-rose-900";
  return (
    <div className={`rounded-2xl border p-5 ${cls}`} data-testid={`nadi-verdict-${tone}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold mb-2">
        <Icon size={14} weight="duotone" /> {label}
      </div>
      <p
        className="text-sm sm:text-base leading-relaxed"
        dangerouslySetInnerHTML={{ __html: linkify(body) }}
      />
    </div>
  );
}

function SectionCard({ secKey, title, narrative, yogas }) {
  const Icon = SECTION_ICONS[secKey] || MoonStars;
  const theme = SECTION_THEME[secKey] || "from-amber-50 to-orange-50 border-amber-200";
  const [open, setOpen] = useState(true);
  const paragraphs = String(narrative || "").split(/\n\n+/).filter((p) => p.trim());

  return (
    <div
      className={`rounded-2xl border bg-gradient-to-br ${theme} overflow-hidden`}
      data-testid={`nadi-section-${secKey}`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-5 sm:px-7 py-4 text-left hover:bg-white/30 transition-colors"
        data-testid={`nadi-section-toggle-${secKey}`}
      >
        <Icon size={28} weight="duotone" className="text-[#3F2D1B] shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="font-serif-display text-xl sm:text-2xl text-[#3F2D1B] leading-tight">
            {title}
          </h3>
          <div className="text-[11px] text-[#3F2D1B]/70 mt-0.5">
            {yogas.length > 0
              ? `${yogas.length} detected yoga${yogas.length === 1 ? "" : "s"} · ${yogas.map((y) => y.name).slice(0, 2).join(" · ")}${yogas.length > 2 ? " …" : ""}`
              : "Read from karakas + planetary positions"}
          </div>
        </div>
        <CaretDown
          size={18}
          weight="bold"
          className={`text-[#3F2D1B] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-5 sm:px-7 pb-6 pt-1 space-y-4 bg-white/40">
          {/* Yoga chips */}
          {yogas.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid={`nadi-section-yogas-${secKey}`}>
              {yogas.map((y) => (
                <span
                  key={y.name}
                  className={`text-[11px] px-2.5 py-1 rounded-full border ${
                    y.polarity === "positive"
                      ? "bg-emerald-100 border-emerald-300 text-emerald-900"
                      : y.polarity === "negative"
                      ? "bg-rose-100 border-rose-300 text-rose-900"
                      : "bg-slate-100 border-slate-300 text-slate-800"
                  }`}
                  title={y.meaning}
                >
                  {y.name}
                </span>
              ))}
            </div>
          )}
          {/* Narrative paragraphs */}
          {paragraphs.length > 0 ? (
            <div className="space-y-3" data-testid={`nadi-section-narrative-${secKey}`}>
              {paragraphs.map((p, i) => (
                <p
                  key={i}
                  className="text-sm sm:text-[15px] leading-relaxed text-[#3F2D1B]"
                  dangerouslySetInnerHTML={{ __html: linkify(p) }}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm italic text-[#3F2D1B]/60">No narrative generated.</p>
          )}
          {/* Engineering details for super-admin */}
          {yogas.length > 0 && (
            <details className="text-xs text-[#3F2D1B]/70">
              <summary className="cursor-pointer">Show classical rules behind each yoga</summary>
              <ul className="mt-2 space-y-1.5">
                {yogas.map((y) => (
                  <li key={y.name}>
                    <b>{y.name}</b> ({y.planets.join("+")}) — {y.meaning}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function PlanetTable({ planets, karakas }) {
  const karakaByPlanet = Object.fromEntries(
    (karakas || []).map((k) => [k.planet, k.karaka])
  );
  return (
    <div className="overflow-x-auto">
      <div className="text-[11px] uppercase tracking-widest text-[#3F2D1B]/70 mb-2 font-semibold">
        Planet table
      </div>
      <table className="w-full text-xs border border-soft rounded-md overflow-hidden">
        <thead className="bg-amber-100/60 text-[#3F2D1B]">
          <tr>
            <th className="text-left px-3 py-2">Planet</th>
            <th className="text-left px-3 py-2">Rashi</th>
            <th className="text-left px-3 py-2">House</th>
            <th className="text-left px-3 py-2">Deg</th>
            <th className="text-left px-3 py-2">Nakshatra</th>
            <th className="text-left px-3 py-2">Karaka</th>
          </tr>
        </thead>
        <tbody>
          {planets.map((p) => (
            <tr key={p.planet} className="border-t border-soft" data-testid={`nadi-planet-row-${p.planet}`}>
              <td className="px-3 py-1.5 font-medium">
                {p.planet}{p.retrograde ? " (R)" : ""}
              </td>
              <td className="px-3 py-1.5">{p.rashi}</td>
              <td className="px-3 py-1.5">{p.house || "—"}</td>
              <td className="px-3 py-1.5">{p.deg}°</td>
              <td className="px-3 py-1.5">{p.nakshatra} ({p.nakshatra_lord})</td>
              <td className="px-3 py-1.5 text-amber-700">{karakaByPlanet[p.planet] || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function YogasTable({ yogas }) {
  if (!yogas.length) return <p className="text-xs italic">No Nadi yogas detected for this chart.</p>;
  return (
    <div className="overflow-x-auto">
      <div className="text-[11px] uppercase tracking-widest text-[#3F2D1B]/70 mb-2 font-semibold">
        All detected yogas
      </div>
      <table className="w-full text-xs border border-soft rounded-md overflow-hidden">
        <thead className="bg-amber-100/60 text-[#3F2D1B]">
          <tr>
            <th className="text-left px-3 py-2">Section</th>
            <th className="text-left px-3 py-2">Yoga</th>
            <th className="text-left px-3 py-2">Planets</th>
            <th className="text-left px-3 py-2">Polarity</th>
            <th className="text-left px-3 py-2">Link</th>
          </tr>
        </thead>
        <tbody>
          {yogas.map((y, i) => (
            <tr key={i} className="border-t border-soft" data-testid={`nadi-yoga-row-${i}`}>
              <td className="px-3 py-1.5">{y.section}</td>
              <td className="px-3 py-1.5 font-medium">{y.name}</td>
              <td className="px-3 py-1.5">{(y.planets || []).join("+")}</td>
              <td className="px-3 py-1.5">
                <span className={
                  y.polarity === "positive" ? "text-emerald-700" :
                  y.polarity === "negative" ? "text-rose-700" : "text-slate-700"
                }>{y.polarity}</span>
              </td>
              <td className="px-3 py-1.5 text-[#3F2D1B]/70">
                {(y.links || []).map((l) => l.kind).join("; ") || "presence"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Minimal markdown-bold + escape — Claude sometimes returns **bold** markers
function linkify(text) {
  if (!text) return "";
  const escaped = String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>");
}

// ───────────────────────────────────────────────────────────────────────
// Nadi-specific: Degree-chain supporters + Destiny-breaker alerts
// ───────────────────────────────────────────────────────────────────────
function DegreeChainPanel({ chain, breakers }) {
  const supporters = (chain || []).filter((r) => r.supporter);
  if (!supporters.length && !breakers?.length) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="nadi-degree-panels">
      {supporters.length > 0 && (
        <div className="card-surface rounded-2xl p-5 sm:p-6" data-testid="nadi-degree-chain">
          <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-3">
            <ChartLineUp size={14} weight="duotone" /> Degree-chain supporters
          </div>
          <p className="text-xs text-[#3F2D1B]/70 mb-3 italic">
            Per Nadi: the planet whose absolute degree immediately PRECEDES another planet supports that planet&apos;s outcomes.
          </p>
          <ul className="space-y-2">
            {supporters.map((r, i) => (
              <li
                key={i}
                className="text-sm text-[#3F2D1B] flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200"
                data-testid={`nadi-supporter-${r.planet}`}
              >
                <b className="text-emerald-800">{r.supporter}</b>
                <span className="text-[#3F2D1B]/60">→ supports →</span>
                <b className="text-emerald-800">{r.planet}</b>
                <span className="text-[11px] text-[#3F2D1B]/60 ml-auto">{r.gap_deg}° gap</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {breakers?.length > 0 && (
        <div className="card-surface rounded-2xl p-5 sm:p-6" data-testid="nadi-destiny-breakers">
          <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-3">
            <Warning size={14} weight="duotone" /> Destiny-breaker alerts (Sanyas-Yoga)
          </div>
          <p className="text-xs text-[#3F2D1B]/70 mb-3 italic">
            Per Nadi: Ketu, Saturn or Mars in the NEXT rashi from a planet creates ups-downs and renunciation pulls for that planet&apos;s domain.
          </p>
          <ul className="space-y-2">
            {breakers.map((b, i) => (
              <li
                key={i}
                className="text-sm text-[#3F2D1B] flex items-start gap-2 px-3 py-2 rounded-md bg-rose-50 border border-rose-200"
                data-testid={`nadi-breaker-${b.planet}`}
              >
                <Warning size={14} weight="bold" className="text-rose-700 mt-0.5 shrink-0" />
                <div>
                  <b className="text-rose-800">{b.breaker}</b>
                  <span className="text-[#3F2D1B]/70"> in </span>
                  <b>{b.breaker_rashi}</b>
                  <span className="text-[#3F2D1B]/70"> sits next to </span>
                  <b className="text-rose-800">{b.planet}</b>
                  <span className="text-[#3F2D1B]/70"> in {b.planet_rashi}</span>
                  <div className="text-[11px] text-[#3F2D1B]/70 mt-0.5">{b.effect}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}



// ───────────────────────────────────────────────────────────────────────
// Section 2 · Transit Predictor result view
// ───────────────────────────────────────────────────────────────────────
const TONE_THEME = {
  rise: {
    chip: "bg-emerald-100 border-emerald-300 text-emerald-900",
    label: "text-emerald-700",
    icon: ArrowUp,
    card: "bg-emerald-50 border-emerald-200",
  },
  fall: {
    chip: "bg-rose-100 border-rose-300 text-rose-900",
    label: "text-rose-700",
    icon: ArrowDown,
    card: "bg-rose-50 border-rose-200",
  },
  test: {
    chip: "bg-amber-100 border-amber-300 text-amber-900",
    label: "text-amber-700",
    icon: ArrowsLeftRight,
    card: "bg-amber-50 border-amber-200",
  },
};

function NadiTransitResultView({ data }) {
  const { forecast, narrative, personalized } = data;
  const windows = forecast?.windows || [];
  const byMonth = forecast?.by_month || {};
  const monthKeys = Object.keys(byMonth).sort();

  return (
    <div className="mt-10 space-y-6" data-testid="nadi-transit-result">
      {/* Hero */}
      <div
        className="rounded-2xl bg-gradient-to-br from-[#3F2D1B] via-[#5a3e22] to-[#7c4f2a] text-[#FAF4E8] p-6 sm:p-8 shadow-xl border border-amber-700/40"
        data-testid="nadi-transit-hero"
      >
        <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-widest mb-2">
          <CalendarBlank size={14} weight="fill" /> Transit Forecast · {forecast.category_title}
        </div>
        <h2 className="font-serif-display text-2xl sm:text-3xl leading-tight">
          {formatDate(forecast.start_date)} <span className="text-amber-300">→</span> {formatDate(forecast.end_date)}
        </h2>
        <div className="mt-2 text-amber-200/80 text-sm">
          {forecast.total_windows} detected windows ·
          {" "}<span className="text-emerald-300">{forecast.tone_summary.rise || 0} rise</span> ·
          {" "}<span className="text-amber-300">{forecast.tone_summary.test || 0} test</span> ·
          {" "}<span className="text-rose-300">{forecast.tone_summary.fall || 0} fall</span>
        </div>
        {narrative?.headline && (
          <p
            className="mt-4 text-sm sm:text-base text-amber-50 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: linkify(narrative.headline) }}
          />
        )}
      </div>

      {!personalized && (
        <div
          className="rounded-2xl border border-amber-400 bg-amber-50 p-4 sm:p-5 flex items-start gap-3"
          data-testid="nadi-transit-llm-warning"
        >
          <Warning size={20} weight="duotone" className="text-amber-700 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900">
            <b>Narrative LLM unavailable.</b> The deterministic transit engine
            has still detected every aspect-hit (see windows below). The most
            common causes are a transient LLM timeout or a truncated reply on
            very large date ranges — try narrowing the range or rerunning.
            {narrative?.llm_error && (
              <div className="text-[11px] mt-1 opacity-70 font-mono break-all">
                Diagnostic: {narrative.llm_error}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bhrigu-Nandi Age-Block panel */}
      <AgeBlockPanel ageBlocks={forecast.age_blocks || {}} forecast={forecast} />

      {/* Phases */}
      {(narrative?.phases || []).length > 0 && (
        <div className="space-y-3" data-testid="nadi-transit-phases">
          <div className="label-eyebrow text-gold flex items-center gap-1.5">
            <Sparkle size={14} weight="duotone" /> Karmic phases within the window
          </div>
          {narrative.phases.map((ph, i) => {
            const theme = TONE_THEME[ph.tone] || TONE_THEME.test;
            const Icon = theme.icon;
            return (
              <div
                key={i}
                className={`rounded-2xl border p-5 ${theme.card}`}
                data-testid={`nadi-transit-phase-${i}`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Icon size={16} weight="bold" className={theme.label} />
                  <span className={`text-[10px] uppercase tracking-widest font-semibold ${theme.label}`}>
                    {ph.tone}
                  </span>
                  <span className="text-[11px] text-[#3F2D1B]/70">
                    {formatDate(ph.from)} → {formatDate(ph.to)}
                  </span>
                </div>
                <h3 className="font-serif-display text-lg sm:text-xl text-[#3F2D1B] leading-tight">
                  {ph.title}
                </h3>
                <p
                  className="mt-2 text-sm sm:text-[15px] leading-relaxed text-[#3F2D1B]/90"
                  dangerouslySetInnerHTML={{ __html: linkify(ph.summary) }}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Watch dates */}
      {(narrative?.watch_dates || []).length > 0 && (
        <div className="card-surface rounded-2xl p-5 sm:p-7" data-testid="nadi-transit-watch-dates">
          <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-3">
            <Clock size={14} weight="duotone" /> Watch these dates
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {narrative.watch_dates.map((w, i) => {
              const theme = TONE_THEME[w.tone] || TONE_THEME.test;
              return (
                <li
                  key={i}
                  className={`rounded-xl border ${theme.card} px-3 py-2.5 flex items-start gap-3`}
                  data-testid={`nadi-transit-watch-${i}`}
                >
                  <div className={`font-serif-display text-sm font-semibold ${theme.label} shrink-0 min-w-[88px]`}>
                    {formatDate(w.date)}
                  </div>
                  <div className="text-xs sm:text-sm text-[#3F2D1B] leading-snug">{w.label}</div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Do / Avoid */}
      {((narrative?.do || []).length > 0 || (narrative?.avoid || []).length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="nadi-transit-do-avoid">
          {(narrative.do || []).length > 0 && (
            <div className="rounded-2xl border bg-emerald-50 border-emerald-200 p-5">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700 mb-2 flex items-center gap-1.5">
                <ArrowUp size={13} weight="bold" /> Do
              </div>
              <ul className="space-y-2">
                {narrative.do.map((d, i) => (
                  <li key={i} className="text-sm text-emerald-900 leading-relaxed" data-testid={`nadi-transit-do-${i}`}>
                    · {d}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(narrative.avoid || []).length > 0 && (
            <div className="rounded-2xl border bg-rose-50 border-rose-200 p-5">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-rose-700 mb-2 flex items-center gap-1.5">
                <ArrowDown size={13} weight="bold" /> Avoid
              </div>
              <ul className="space-y-2">
                {narrative.avoid.map((d, i) => (
                  <li key={i} className="text-sm text-rose-900 leading-relaxed" data-testid={`nadi-transit-avoid-${i}`}>
                    · {d}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Month-by-month timeline */}
      {monthKeys.length > 0 && (
        <div className="card-surface rounded-2xl p-5 sm:p-7" data-testid="nadi-transit-timeline">
          <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-4">
            <CalendarBlank size={14} weight="duotone" /> Month-by-month timeline ({windows.length} windows)
          </div>
          <div className="space-y-5">
            {monthKeys.map((mk) => (
              <div key={mk} data-testid={`nadi-transit-month-${mk}`}>
                <div className="text-xs uppercase tracking-widest text-[#3F2D1B]/70 font-semibold mb-2">
                  {formatMonth(mk)}
                </div>
                <ul className="space-y-2">
                  {byMonth[mk].map((w, i) => {
                    const theme = TONE_THEME[w.tone] || TONE_THEME.test;
                    return (
                      <li
                        key={i}
                        className={`rounded-xl border ${theme.card} p-3 flex items-start gap-3`}
                        data-testid={`nadi-transit-window-${w.peak_date}-${w.transit}-${w.natal}`}
                      >
                        <div className={`text-xs font-semibold ${theme.label} shrink-0 min-w-[72px]`}>
                          {w.peak_date.slice(8, 10)} {monthShort(w.peak_date)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-[#3F2D1B]">
                            <b>{w.transit}</b>{w.peak_retro ? " (R)" : ""}{" "}
                            <span className="text-[#3F2D1B]/60">on</span>{" "}
                            <b>{w.natal}</b>
                            {w.natal_via ? <span className="text-[#3F2D1B]/60"> ({w.natal_via})</span> : null}
                            <span className={`ml-2 inline-block text-[10px] uppercase tracking-widest font-bold ${theme.label}`}>
                              {w.tone}
                            </span>
                            {w.bhrigu_pinpoint && (
                              <span
                                className="ml-2 inline-block text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded-full"
                                title="Bhrigu pinpoint: real transit + age-block align"
                                data-testid={`nadi-pinpoint-${w.peak_date}`}
                              >
                                ⭐ Bhrigu Pinpoint
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-[#3F2D1B]/60 mt-0.5">
                            {w.aspect_label} · {formatDate(w.start_date)} → {formatDate(w.end_date)}
                            {w.age_at_peak ? <span> · age {w.age_at_peak}</span> : null}
                          </div>
                          <div className="text-xs text-[#3F2D1B]/80 mt-1.5 leading-relaxed">
                            {w.rule}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}
function formatMonth(yyyyMm) {
  try {
    const d = new Date(`${yyyyMm}-01T00:00:00`);
    return d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  } catch {
    return yyyyMm;
  }
}
function monthShort(iso) {
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString("en-IN", { month: "short" });
  } catch {
    return "";
  }
}

// ───────────────────────────────────────────────────────────────────────
// Bhrigu-Nandi Age-Block Panel
// ───────────────────────────────────────────────────────────────────────
const PLANET_COLOUR = {
  Jupiter: "bg-amber-100 border-amber-300 text-amber-900",
  Saturn:  "bg-slate-100 border-slate-300 text-slate-900",
  Rahu:    "bg-purple-100 border-purple-300 text-purple-900",
  Ketu:    "bg-rose-100 border-rose-300 text-rose-900",
};

function AgeBlockPanel({ ageBlocks, forecast }) {
  const planets = Object.keys(ageBlocks || {});
  if (!planets.length) return null;
  // Highlight blocks whose age range overlaps the forecast window
  const startYr = forecast?.start_date ? new Date(forecast.start_date).getFullYear() : null;
  return (
    <div className="card-surface rounded-2xl p-5 sm:p-7" data-testid="nadi-age-blocks">
      <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-3">
        <Clock size={14} weight="duotone" /> Bhrigu-Nandi age-block projection
      </div>
      <p className="text-xs text-[#3F2D1B]/70 mb-4 italic">
        Each slow planet projects an age-window onto successive rashis from your natal placement.
        Jupiter = 12 yrs/sign, Saturn = 30 yrs/sign, Rahu / Ketu = 18 yrs/sign.
        When a real transit aspect hits during the same planet&apos;s active age-block,
        the event is a <b>Bhrigu Pinpoint</b>.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {planets.map((planet) => (
          <div key={planet} data-testid={`nadi-age-block-${planet}`}>
            <div className="text-xs uppercase tracking-widest text-[#3F2D1B]/70 font-semibold mb-2">
              {planet} {ageBlocks[planet][0]?.retro ? "(Retrograde · moves backward)" : ""}
            </div>
            <ul className="space-y-1.5">
              {ageBlocks[planet].slice(0, 9).map((b, i) => (
                <li
                  key={i}
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-xs ${PLANET_COLOUR[planet] || "bg-slate-100 border-slate-200 text-slate-900"}`}
                >
                  <span className="font-semibold min-w-[60px]">
                    {b.from_age}–{b.to_age}
                  </span>
                  <span className="font-medium">{b.rashi}</span>
                  {b.partial && (
                    <span className="text-[10px] uppercase tracking-widest opacity-70">partial</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

